// Money across currencies, for adding up and ranking only: the leaderboard
// ranks everyone on one basis, and a goal set in one currency counts earnings
// made in another. The rates are the team's (fx_rates, passed in), with a
// rough fixed one for a currency they lack. Nothing is ever paid at these
// rates; a fee is always paid in the currency it was fixed in. Every amount
// is in minor units (a forint too is stored ×100). Also which currency a
// country's creators get until they pick one (docs/app-concept.md §16), and
// how a whole-job fee splits across its videos. Pure.

import {isOneOf} from '../core/config.ts';

/** What a creator may pick to see money in. */
export const DISPLAY_CURRENCIES: readonly string[] = Object.freeze(['USD', 'EUR', 'HUF', 'GBP', 'PLN', 'CZK', 'RON', 'SEK', 'DKK']);

/** Every currency used by one of the campaign markets. */
export const MARKET_CURRENCIES: readonly string[] = Object.freeze([
  'EUR', 'AUD', 'BRL', 'CAD', 'CHF', 'CZK', 'DKK', 'GBP', 'HUF', 'ILS', 'ISK', 'JPY', 'KRW',
  'MXN', 'NOK', 'NZD', 'PLN', 'RON', 'RSD', 'SEK', 'SGD', 'TRY', 'UAH', 'USD', 'ZAR',
]);

/** The 21 euro countries (Bulgaria since 1 January 2026). */
export const EURO_COUNTRIES: readonly string[] = Object.freeze([
  'AT', 'BE', 'BG', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
]);

/** The app's countries with a currency of their own. */
const NATIONAL_CURRENCIES: Readonly<Record<string, string>> = Object.freeze({
  US: 'USD', HU: 'HUF', PL: 'PLN', CZ: 'CZK', RO: 'RON', SE: 'SEK', DK: 'DKK', GB: 'GBP',
});

/** The commercial currency used for a rate in each supported creator market. */
const MARKET_NATIONAL_CURRENCIES: Readonly<Record<string, string>> = Object.freeze({
  AU: 'AUD', BR: 'BRL', CA: 'CAD', CH: 'CHF', CZ: 'CZK', DK: 'DKK', GB: 'GBP', HU: 'HUF',
  IL: 'ILS', IS: 'ISK', JP: 'JPY', KR: 'KRW', LI: 'CHF', MX: 'MXN', NO: 'NOK', NZ: 'NZD',
  PL: 'PLN', RO: 'RON', RS: 'RSD', SE: 'SEK', SG: 'SGD', TR: 'TRY', UA: 'UAH', US: 'USD', ZA: 'ZAR',
});

/** The local currency in which a country's campaign offer is entered and shown. */
export function marketCurrencyForCountry(country: string | null | undefined): string {
  const code = String(country ?? '').trim().toUpperCase();
  return MARKET_NATIONAL_CURRENCIES[code] ?? (EURO_COUNTRIES.includes(code) ? 'EUR' : 'USD');
}

/** The currency a creator in `country` is quoted and paid in until they pick one; dollars outside the app's countries. */
export function currencyForCountry(country: string | null | undefined): string {
  const code = String(country ?? '').trim().toUpperCase();
  return NATIONAL_CURRENCIES[code] ?? (EURO_COUNTRIES.includes(code) ? 'EUR' : 'USD');
}

/** The same mapping as SQL over a country expression, for writes that set it in the database. */
export function currencyForCountrySql(country: string): string {
  const pairs = [...Object.entries(NATIONAL_CURRENCIES), ...EURO_COUNTRIES.map((code) => [code, 'EUR'])];
  return `CASE UPPER(TRIM(${country})) ${pairs.map(([code, currency]) => `WHEN '${code}' THEN '${currency}'`).join(' ')} ELSE 'USD' END`;
}

/** Units of each currency per euro, as the team set them (fx_rates). */
export type RatesPerEur = Readonly<Record<string, number>>;

/** Roughly what one unit is worth in euros, for a currency the team's rates lack. Close enough to rank by, never to pay by. */
const EUR_PER_UNIT: Readonly<Record<string, number>> = Object.freeze({
  EUR: 1,
  USD: 0.92,
  GBP: 1.17,
  HUF: 0.0025,
  PLN: 0.23,
  CZK: 0.04,
  RON: 0.2,
  SEK: 0.088,
  DKK: 0.134,
  AUD: 0.62,
  BRL: 0.17,
  CAD: 0.63,
  CHF: 1.08,
  ILS: 0.29,
  ISK: 0.0073,
  JPY: 0.0057,
  KRW: 0.00066,
  MXN: 0.049,
  NOK: 0.092,
  NZD: 0.5,
  RSD: 0.0085,
  SGD: 0.7,
  TRY: 0.018,
  UAH: 0.02,
  ZAR: 0.053,
});

export function isDisplayCurrency(value: unknown): value is string {
  return isOneOf(DISPLAY_CURRENCIES, value);
}

function eurPerUnit(currency: string, rates: RatesPerEur): number | undefined {
  const perEur = rates[currency];
  return perEur && perEur > 0 ? 1 / perEur : EUR_PER_UNIT[currency];
}

/** An amount in another currency at the team's rates, rounded to whole minor units. Unknown currencies pass through. */
export function convertCents(cents: number, from: string, to: string, rates: RatesPerEur = {}): number {
  if (from === to) return cents;
  const fromRate = eurPerUnit(from, rates);
  const toRate = eurPerUnit(to, rates);
  if (!fromRate || !toRate) return cents;
  return Math.round((cents * fromRate) / toRate);
}

/** Amounts in several currencies added up in one. */
export function sumIn(amounts: readonly {cents: number; currency: string}[], currency: string, rates: RatesPerEur = {}): number {
  return amounts.reduce((total, amount) => total + convertCents(amount.cents, amount.currency, currency, rates), 0);
}

/**
 * One video's part of a fee paid for a whole job of `videos` videos: an even
 * share, rounded down, and for the video that completes the job what the
 * others leave of the fee. That is never below a share nor above a share and
 * the leftover cents, so a video priced by hand elsewhere changes nothing here.
 * `others` are the job's other videos still in play (not turned down).
 */
export function jobVideoShare(amount: number, videos: number, others: {count: number; cents: number}): number {
  const count = Math.max(1, videos);
  const share = Math.floor(amount / count);
  if (others.count < count - 1) return share;
  const leftover = amount - share * count;
  return Math.min(share + leftover, Math.max(share, amount - others.cents));
}
